import React, {
  useState, useCallback, useEffect, useRef,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import CommunityErrorFallback from '../../../../shared/ErrorHandling/CommunityErrorFallback/index'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../../../../../Loading/index'
import { usePostHogEvent } from '../../../../../PostHog/index'
import { useTranslatedText } from '../../../../../StaticText/StaticTextContext'
import NotificationToast from '../../../../../NotificationToast/index'
import {
  getToastExitClass, TOAST_FADE_MS, TOAST_COLLAPSE_MS,
} from '../PlaylistTabContainer/toast-exit-utils'
import FollowedPlaylistRow from '../../../shared/PlaylistRow/FollowedPlaylistRow'
import CommunityEmptyState from '../../../shared/CommunityEmptyState/CommunityEmptyState'
import LoadMore from '../../../../../LoadMore/index'
import type { FollowedPlaylistShellNode } from '../../../../../../services/community/types'

/**
 * Following tab — list of followed playlists.
 *
 * Per-row hydration via the FollowedPlaylistRow variant. Sibling rows do
 * not commit when one row's hydration completes.
 *
 * The toast-target row stays inline because its class names + inline
 * minHeight change per animation phase, and at most one row is in the
 * toast state at a time — folding it into the variant would just add
 * conditional rendering for no win.
 */
export interface ProfileFollowingTabProps {
  followedPlaylists: FollowedPlaylistShellNode[]
  followedPlaylistsProcessing: boolean
  followedPlaylistsTotalCount?: number | null
  searchQuery: string
  language: string
  jwt?: string
  unfollowToast: { playlistId: string; playlistName: string } | null
  onUnfollow: (id: string, name: string) => void
  onToastComplete: (id: string) => void
  reFollowPlaylist: (() => Promise<void>) | null
  removeFollowedPlaylist: ((id: string) => void) | null
  loadMoreFollowedPlaylists?: () => void
  hasMoreFollowedPlaylists?: boolean
  isLoadingMoreFollowedPlaylists?: boolean
  seeMoreLabel: string
  showLessLabel: string
}

function ProfileFollowingTab ({
  followedPlaylists,
  followedPlaylistsProcessing,
  followedPlaylistsTotalCount,
  searchQuery,
  language,
  jwt,
  unfollowToast,
  onUnfollow,
  onToastComplete,
  reFollowPlaylist,
  removeFollowedPlaylist,
  loadMoreFollowedPlaylists,
  hasMoreFollowedPlaylists,
  isLoadingMoreFollowedPlaylists,
  seeMoreLabel,
  showLessLabel,
}: ProfileFollowingTabProps) {
  // i18n via the nearest <StaticTextProvider> (set up by PlaylistTabContainer's
  // Outer with the `profileCollectionsTabs` slice).
  const followingEmptyText = useTranslatedText({ keys: ['followingEmpty'] })
  const searchNoResultsText = useTranslatedText({ keys: ['searchNoResults'] })
  const noLongerFollowingPrefix = useTranslatedText({ keys: ['noLongerFollowing'] })
  const undoLabel = useTranslatedText({ keys: ['undo'] })
  const byAuthorTemplate = useTranslatedText({ keys: ['byAuthor'] })
  const byAnonymousLabel = useTranslatedText({ keys: ['byAnonymous'] })

  // Ref map to track playlist row DOM elements for height capture.
  const rowElMap = useRef<Map<string, HTMLDivElement | null>>(new Map())
  const trackPlaylistItemPlayed = usePostHogEvent('community_playlist_item_played')

  const setRowRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) rowElMap.current.set(id, el)
    else rowElMap.current.delete(id)
  }, [])

  // Captured height of the row being unfollowed.
  const [toastRowHeight, setToastRowHeight] = useState<number | null>(null)

  // Exit animation phase.
  const [toastExitPhase, setToastExitPhase] = useState<'idle' | 'fading' | 'collapsing'>('idle')
  const followingFadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const followingCollapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unfollowToastRef = useRef(unfollowToast)
  unfollowToastRef.current = unfollowToast

  useEffect(() => () => {
    if (followingFadeTimerRef.current) clearTimeout(followingFadeTimerRef.current)
    if (followingCollapseTimerRef.current) clearTimeout(followingCollapseTimerRef.current)
  }, [])

  const handleUnfollow = useCallback((playlistId: string, playlistName: string) => {
    // Capture row height before state change causes re-render.
    const rowEl = rowElMap.current.get(playlistId)
    setToastRowHeight(rowEl ? rowEl.offsetHeight : null)
    setToastExitPhase('idle')
    onUnfollow(playlistId, playlistName)
  }, [onUnfollow])

  const handleFollowingToastUndo = useCallback(() => {
    if (followingFadeTimerRef.current) {
      clearTimeout(followingFadeTimerRef.current)
      followingFadeTimerRef.current = null
    }
    if (followingCollapseTimerRef.current) {
      clearTimeout(followingCollapseTimerRef.current)
      followingCollapseTimerRef.current = null
    }
    setToastExitPhase('idle')
    setToastRowHeight(null)
    if (reFollowPlaylist) {
      reFollowPlaylist()
    }
    onToastComplete(unfollowToastRef.current?.playlistId || '')
  }, [reFollowPlaylist, onToastComplete])

  const handleFollowingToastDismiss = useCallback(() => {
    setToastExitPhase('fading')

    followingFadeTimerRef.current = setTimeout(() => {
      followingFadeTimerRef.current = null
      setToastExitPhase('collapsing')

      followingCollapseTimerRef.current = setTimeout(() => {
        followingCollapseTimerRef.current = null
        setToastExitPhase('idle')
        setToastRowHeight(null)

        const toast = unfollowToastRef.current
        if (toast && removeFollowedPlaylist) {
          removeFollowedPlaylist(toast.playlistId)
        }
        onToastComplete(toast?.playlistId || '')
      }, TOAST_COLLAPSE_MS)
    }, TOAST_FADE_MS)
  }, [removeFollowedPlaylist, onToastComplete])

  return (
    <div className="playlist-tab-container__following">
      {followedPlaylistsProcessing && followedPlaylists.length === 0 ? (
        <div className="playlist-tab-container__following-loading">
          <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
        </div>
      ) : null}

      {!followedPlaylistsProcessing && followedPlaylists.length === 0 && !searchQuery ? (
        <CommunityEmptyState variant="full-height" description={followingEmptyText} />
      ) : null}

      {!followedPlaylistsProcessing && followedPlaylists.length === 0 && searchQuery ? (
        <CommunityEmptyState variant="full-height" description={searchNoResultsText} />
      ) : null}

      {followedPlaylists.length > 0 && !followedPlaylistsProcessing ? (
        <div className="playlist-tab-container__following-list">
          {followedPlaylists.map((playlist) => {
            const isToastTarget = unfollowToast?.playlistId === playlist.id

            if (isToastTarget) {
              const exitClass = getToastExitClass(toastExitPhase)
              return (
                <div
                  key={playlist.id}
                  ref={(el) => setRowRef(playlist.id, el)}
                  className={`playlist-tab-container__playlist-row playlist-tab-container__playlist-row--toast${exitClass}`}
                  style={toastRowHeight ? { minHeight: toastRowHeight } : undefined}
                >
                  <NotificationToast
                    duration={5000}
                    message={(
                      <span>
                        {noLongerFollowingPrefix}
                        <strong>{unfollowToast.playlistName}</strong>
                      </span>
                    )}
                    variant="success"
                    onUndo={handleFollowingToastUndo}
                    undoLabel={undoLabel}
                    onDismiss={handleFollowingToastDismiss}
                  />
                </div>
              )
            }

            const authorName = playlist.userProfile?.displayName || null
            const authorDisplay = authorName
              ? byAuthorTemplate.replace('{name}', authorName)
              : byAnonymousLabel

            return (
              // Per-row error isolation — a crash in one followed-playlist
              // row no longer takes down the whole Following tab.
              <ErrorBoundary
                key={playlist.id}
                FallbackComponent={CommunityErrorFallback}
                resetKeys={[playlist.id]}
              >
                <FollowedPlaylistRow
                  playlist={playlist}
                  language={language}
                  jwt={jwt}
                  authorDisplay={authorDisplay}
                  seeMoreLabel={seeMoreLabel}
                  showLessLabel={showLessLabel}
                  onUnfollow={handleUnfollow}
                  onRowMount={setRowRef}
                  trackPlaylistItemPlayed={trackPlaylistItemPlayed}
                />
              </ErrorBoundary>
            )
          })}
        </div>
      ) : null}

      {loadMoreFollowedPlaylists
        && hasMoreFollowedPlaylists
        && followedPlaylists.length > 0
        && !followedPlaylistsProcessing ? (
          // @ts-expect-error LoadMore's default export carries a bogus `@type {LoadMore}`
          // JSDoc (lib/components/LoadMore/LoadMore.jsx) that types the connected export as a
          // class instance, not a component. Suppressed here to avoid editing shared infra.
          <LoadMore
            onLoadMore={loadMoreFollowedPlaylists}
            currentCount={followedPlaylists.length}
            totalCount={followedPlaylistsTotalCount ?? followedPlaylists.length}
            processing={!!isLoadingMoreFollowedPlaylists}
            buttonClassName={['playlist-tab-container__load-more-button', 'button--secondary']}
            infiniteScroll
          />
        ) : null}
    </div>
  )
}

export default React.memo(ProfileFollowingTab)
